import React, { useEffect, useMemo, useState } from 'react';
import { Beer } from '../../state/types';
import {
  createRouletteSegments,
  getSpinDegrees,
  pickSpinTarget
} from '../../utils/brewletteLogic';
import styles from './Brewlette.module.css';

interface BrewletteProps {
  open: boolean;
  beers: Beer[];
  onClose: () => void;
  onBeerSelected: (beer: Beer) => void;
  suppressStageOverlay?: boolean;
}

const SEGMENT_ANGLE = 10;

const Brewlette: React.FC<BrewletteProps> = ({
  open,
  beers,
  onClose,
  onBeerSelected,
  suppressStageOverlay = false
}) => {
  const [rotation, setRotation] = useState(0);

  const segments = useMemo(() => createRouletteSegments(beers), [beers]);

  useEffect(() => {
    let revealTimer: number | null = null;

    if (!open) {
      setRotation(0);
      return;
    }

    if (beers.length === 0) {
      return;
    }

    const startTimer = window.setTimeout(() => {
      const target = pickSpinTarget(segments);
      const spinDegrees = getSpinDegrees(target.segmentIndex, 6, 36);
      setRotation(spinDegrees);

      revealTimer = window.setTimeout(() => {
        onBeerSelected(target.beer);
      }, 3000);
    }, 300);

    return () => {
      window.clearTimeout(startTimer);
      if (revealTimer !== null) {
        window.clearTimeout(revealTimer);
      }
    };
  }, [open, beers, segments, onBeerSelected]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className={`${styles.stage} ${suppressStageOverlay ? styles.stageNoOverlay : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Brewlette roulette wheel"
      onClick={onClose}
    >
      <div className={styles.wheelViewport}>
        <div className={styles.pointer} aria-hidden="true" />
        <div data-testid="brewlette-wheel" className={styles.wheel} style={{ transform: `rotate(${rotation}deg)` }}>
          {segments.map((segment) => {
            return (
              <div
                key={segment.index}
                data-testid="brewlette-segment"
                className={`${styles.segment} ${segment.index % 2 === 0 ? styles.segmentPrimary : styles.segmentSecondary}`}
                style={{ transform: `rotate(${segment.index * SEGMENT_ANGLE}deg) skewY(${90 - SEGMENT_ANGLE}deg)` }}
              />
            );
          })}

          {segments.map((segment) => {
            const displayNumber = segment.beers[0]?.number ?? segment.index + 1;
            const numberText = String(displayNumber);
            const markerAngle = segment.index * SEGMENT_ANGLE + SEGMENT_ANGLE / 2;
            const numberClass = segment.index % 2 === 0 ? styles.segmentNumberOnPrimary : styles.segmentNumberOnSecondary;
            const markerRadians = ((markerAngle - 90) * Math.PI) / 180;
            const markerRadius = 44;
            const markerX = 50 + Math.cos(markerRadians) * markerRadius;
            const markerY = 50 + Math.sin(markerRadians) * markerRadius;

            return (
              <div
                key={`marker-${segment.index}`}
                className={styles.segmentNumberMarker}
                style={{
                  left: `${markerX}%`,
                  top: `${markerY}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <span
                  className={`${styles.segmentNumberText} ${numberClass} ${numberText.length >= 3 ? styles.segmentNumberTriple : ''} ${numberText.length === 2 ? styles.segmentNumberDouble : ''}`}
                  style={{ transform: `rotate(${markerAngle}deg)` }}
                >
                  {numberText}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Brewlette;
